import { IconButton, Stack, Tooltip } from "@mui/material";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import { useTranslation } from "react-i18next";
import { useDeleteEventMutation } from "src/api/purchasing/events";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import EventAttachmentsPopup from "../../attachments-popup";
import { oneBranchHasPermission } from "src/utils";
import {
  EventsPermissions,
  PurchasingPermissions,
} from "src/constants/permissions";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import InventoryEventPopup from "../../inventory-event";
import FixedAssetEventPopup from "../../fixed-asset-event";
import { EventTypes } from "src/types/events/events";

interface IAction {
  row: any;
  refetch: Function;
}

const TableActions = (props: IAction) => {
  const { t } = useTranslation("general");
  const { row, refetch } = props;
  const { id, reference } = row;
  const [openDelete, setOpenDelete] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openAttachmentsModal, setOpenAttachmentsModal] = useState(false);

  const [notifications, setNotifications] = useRecoilState(notificationsState);

  // APIS
  const { mutate, isPending, status, error } = useDeleteEventMutation();

  //permissions
  const viewFixedAssetEventPermission = oneBranchHasPermission(
    EventsPermissions.viewFixedAssetEvent,
  );
  const viewInventoryEventPermission = oneBranchHasPermission(
    EventsPermissions.viewInventoryEvent,
  );

  const deleteOwnerEventPermission = oneBranchHasPermission(
    EventsPermissions.deleteOwnerEvent,
  );
  const deleteSupplierEventPermission = oneBranchHasPermission(
    EventsPermissions.deleteSupplierEvent,
  );
  const deletePaymentEventPermission = oneBranchHasPermission(
    EventsPermissions.deletePaymentEvent,
  );
  const deleteExpenseEventPermission = oneBranchHasPermission(
    EventsPermissions.deleteExpenseEvent,
  );
  const deleteFixedAssetEventPermission = oneBranchHasPermission(
    EventsPermissions.deleteFixedAssetEvent,
  );
  const deleteVendorEventPermission = oneBranchHasPermission(
    EventsPermissions.deleteVendorEvent,
  );
  const deleteOtherRevenueEventPermission = oneBranchHasPermission(
    EventsPermissions.deleteOtherRevenueEvent,
  );
  const deleteInventoryEventPermission = oneBranchHasPermission(
    EventsPermissions.deleteInventoryEvent,
  );
  const deleteEmployeeEventPermission = oneBranchHasPermission(
    EventsPermissions.deleteEmployeeEvent,
  );

  const canDeleteEvent =
    {
      owner: deleteOwnerEventPermission,
      supplier: deleteSupplierEventPermission,
      payment: deletePaymentEventPermission,
      liability: deleteExpenseEventPermission,
      fixed_asset: deleteFixedAssetEventPermission,
      vendor: deleteVendorEventPermission,
      other_revenue: deleteOtherRevenueEventPermission,
      inventory: deleteInventoryEventPermission,
      employee: deleteEmployeeEventPermission,
    }[row.type as EventTypes] ?? false;

  const handleOpenDelete = () => setOpenDelete(true);
  const handleCloseDelete = () => setOpenDelete(false);

  const handleOpenView = () => setOpenView(true);
  const handleCloseView = () => setOpenView(false);

  const handleOpenAttachmentsModal = () => setOpenAttachmentsModal(true);
  const handleCloseAttachmentsModal = () => setOpenAttachmentsModal(false);

  const handleDelete = () => {
    mutate(id);
  };

  useEffect(() => {
    if (status === "success") {
      refetch();
      handleCloseDelete();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("deletedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  return (
    <Stack direction={"row"} spacing={"12px"} justifyContent={"flex-start"}>
      <Tooltip title={t("attachments")}>
        <IconButton onClick={handleOpenAttachmentsModal}>
          <img src={DownloadIcon} alt="" />
        </IconButton>
      </Tooltip>

      {canDeleteEvent && (
        <Tooltip title={t("delete")}>
          <IconButton onClick={handleOpenDelete}>
            <img src={DeleteIcon} alt="" />
          </IconButton>
        </Tooltip>
      )}

      {((row.type === "fixed_asset" && viewFixedAssetEventPermission) ||
        row.type === "inventory" ||
        viewInventoryEventPermission) && (
        <Tooltip title={t("details")}>
          <IconButton onClick={handleOpenView}>
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* confirm delete popup */}
      <ConfirmPopup
        open={openDelete}
        item={reference}
        handleClose={handleCloseDelete}
        handleConfirm={handleDelete}
        isPending={isPending}
      />
      {/* view inventory popup */}
      {openView && row.type === "inventory" && (
        <InventoryEventPopup
          open={openView}
          handleClose={handleCloseView}
          eventId={id}
          refetch={refetch}
        />
      )}
      {/* view fixed asset popup */}
      {openView && row.type === "fixed_asset" && (
        <FixedAssetEventPopup
          open={openView}
          handleClose={handleCloseView}
          eventId={id}
          refetch={refetch}
        />
      )}
      {/* attachments popup */}
      {openAttachmentsModal && (
        <EventAttachmentsPopup
          open={openAttachmentsModal}
          handleClose={handleCloseAttachmentsModal}
          rowId={row.id}
        />
      )}
    </Stack>
  );
};
export default TableActions;
